import { useState, useCallback, useEffect, useRef } from 'react';
import { nanoid } from 'nanoid';
import type { ConversationItem } from '../../types/conversationItem.types';
import { useTranslate } from '../../lib/i18n';
import { useCreateResearchConsent } from '../mutations/useCreateResearchConsent';

interface ResearchConsentState {
  hasShown: boolean;
  messageId: string | null;
  isExpanded: boolean;
  hasResponded: boolean;
  currentConsent: boolean | null;
  botMessageCount: number;
  originalMessageParts: {
    messageText: string;
    linkText: string;
    linkUrl: string;
  } | null;
}

interface ResearchConsentActions {
  checkAndShowConsent: (
    response: ConversationItem,
    addToConversation: (message: ConversationItem) => void,
    setConversation: React.Dispatch<React.SetStateAction<ConversationItem[]>>
  ) => void;
  handleConsentResponse: (
    consentGiven: boolean,
    setConversation: React.Dispatch<React.SetStateAction<ConversationItem[]>>
  ) => void;
  handleConsentToggle: () => void;
  reset: () => void;
}

interface UseResearchConsentReturn {
  state: ResearchConsentState;
  actions: ResearchConsentActions;
}

const TYPING_INDICATOR_DELAY = 1000;

const initialState: ResearchConsentState = {
  hasShown: false,
  messageId: null,
  isExpanded: false,
  hasResponded: false,
  currentConsent: null,
  botMessageCount: 0,
  originalMessageParts: null,
};

export const useResearchConsent = (
  language: 'en' | 'fi' | 'sv',
  conversationId: string | null,
  enabled: boolean
): UseResearchConsentReturn => {
  const [state, setState] = useState<ResearchConsentState>(initialState);
  const t = useTranslate();
  const { mutate: createResearchConsent } = useCreateResearchConsent();
  const consentTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to show the research consent prompt
  const showConsentPrompt = useCallback(
    (
      addToConversation: (message: ConversationItem) => void,
      setConversation: React.Dispatch<React.SetStateAction<ConversationItem[]>>
    ) => {
      const consentMessageId = nanoid();

      const messageText = t('researchConsent.message');
      const linkText = t('researchConsent.linkText');
      const linkUrl = t('researchConsent.linkUrl');

      setState((prev) => ({
        ...prev,
        hasShown: true,
        messageId: consentMessageId,
        isExpanded: false,
        hasResponded: false,
        originalMessageParts: {
          messageText,
          linkText,
          linkUrl,
        },
      }));

      // Add "writing..." message immediately
      const writingMessage: ConversationItem = {
        id: consentMessageId,
        author: 'ASSISTANT',
        content: t('common.labels.writing'),
        time: new Date().toUTCString(),
      };
      addToConversation(writingMessage);

      // Update with actual consent message after delay
      consentTimeoutRef.current = setTimeout(() => {
        const markdownContent = `${messageText} [${linkText}](${linkUrl}).`;

        setConversation((prev) =>
          prev.map((msg) =>
            msg.id === consentMessageId
              ? {
                  ...msg,
                  content: markdownContent,
                  special: 'research-consent',
                  time: new Date().toUTCString(),
                }
              : msg
          )
        );
        consentTimeoutRef.current = null;
      }, TYPING_INDICATOR_DELAY);
    },
    [t]
  );

  // Check if consent should be shown and trigger it
  const checkAndShowConsent = useCallback(
    (
      response: ConversationItem,
      addToConversation: (message: ConversationItem) => void,
      setConversation: React.Dispatch<React.SetStateAction<ConversationItem[]>>
    ) => {
      if (
        enabled &&
        !state.hasShown &&
        response.content !== t('common.labels.writing') &&
        response.author === 'ASSISTANT'
      ) {
        const newCount = state.botMessageCount + 1;
        setState((prev) => ({ ...prev, botMessageCount: newCount }));

        // Trigger consent on the third valid bot message (not second)
        if (newCount === 2) {
          showConsentPrompt(addToConversation, setConversation);
        }
      }
    },
    [enabled, state.hasShown, state.botMessageCount, t, showConsentPrompt]
  );

  // Handle consent response
  const handleConsentResponse = useCallback(
    (
      consentGiven: boolean,
      setConversation: React.Dispatch<React.SetStateAction<ConversationItem[]>>
    ) => {
      setState((prev) => ({
        ...prev,
        currentConsent: consentGiven,
        hasResponded: true,
        isExpanded: false,
      }));

      // Update the consent message with thank you text
      if (state.messageId && state.originalMessageParts) {
        setConversation((prev) =>
          prev.map((msg) =>
            msg.id === state.messageId
              ? {
                  ...msg,
                  content: (() => {
                    const { messageText, linkText, linkUrl } = state.originalMessageParts!;
                    const baseContent = `${messageText} [${linkText}](${linkUrl}).`;
                    const thanksForConsent = t('researchConsent.thanksForConsent');
                    const thanksForResponse = t('researchConsent.thanksForResponse');
                    const thankYouMessage = consentGiven ? thanksForConsent : thanksForResponse;
                    return `${baseContent}\n\n${thankYouMessage}`;
                  })(),
                }
              : msg
          )
        );
      }

      // Send consent to backend
      if (conversationId) {
        createResearchConsent({
          conversationId,
          consent: consentGiven,
          language,
        });
      }
    },
    [state.messageId, conversationId, language, t, createResearchConsent]
  );

  // Handle consent card toggle
  const handleConsentToggle = useCallback(() => {
    setState((prev) => ({ ...prev, isExpanded: !prev.isExpanded }));
  }, []);

  // Reset state (useful for testing or re-initialization)
  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (consentTimeoutRef.current) {
        clearTimeout(consentTimeoutRef.current);
        consentTimeoutRef.current = null;
      }
    };
  }, []);

  const actions: ResearchConsentActions = {
    checkAndShowConsent,
    handleConsentResponse,
    handleConsentToggle,
    reset,
  };

  return {
    state,
    actions,
  };
};
